<template>
  <div class="lead-form-page">
    <van-nav-bar :title="editing ? '编辑客资' : '新建客资'" left-arrow @click-left="goBack" />
    <div class="sync-notice"><van-icon name="info-o" /> 企业客户标签会先保存为待同步；关联负责人企微客户后同步。建议让客户扫描客资专属二维码，已添加的客户可在详情刷新关联状态。</div>
    <div v-if="loading" class="form-state"><van-loading size="24px">加载中...</van-loading></div>
    <div v-else-if="loadError" class="form-state">
      <p>{{ loadErrorMessage || '客资资料加载失败' }}</p>
      <van-button type="info" size="small" @click="loadPage">重新加载</van-button>
    </div>
    <van-form v-else ref="leadForm" @submit="submit">
      <section class="form-section">
        <h2>联系人</h2>
        <van-field v-model.trim="form.name" label="姓名" placeholder="未命名客资（可留空）" maxlength="50" />
        <van-field v-model.trim="form.mobile" label="手机号" type="tel" maxlength="30" required :placeholder="editing && original.mobile ? `当前 ${original.mobile}，输入新号码可替换` : '请输入手机号'" :rules="[{ validator: validMobile, message: '请输入有效手机号' }]" />
        <div v-if="mobileRevealError" class="identity-warning">{{ mobileRevealError }}</div>
        <van-field
          v-model.trim="form.wechatId"
          label="微信号"
          maxlength="64"
          :disabled="clearWechatId"
          :placeholder="wechatIdPlaceholder"
          @input="onWechatIdInput"
        />
        <van-cell v-if="hasStoredWechatId" class="wechat-clear-row" title="清除已保存微信号">
          <van-switch slot="right-icon" v-model="clearWechatId" size="20px" @change="onClearWechatIdChange" />
        </van-cell>
        <div v-if="clearWechatId" class="identity-clear-help">保存后将清除该客资已保存的微信号。</div>
        <van-field v-model.trim="form.email" label="邮箱" type="email" maxlength="100" :placeholder="editing && original.email ? `当前 ${original.email}，输入新邮箱可替换` : '选填'" :rules="[{ validator: validEmail, message: '邮箱格式不正确' }]" />
        <van-field v-model.trim="form.telephone" label="座机" maxlength="30" :placeholder="editing && original.telephone ? `当前 ${original.telephone}，输入新号码可替换` : '选填'" />
        <van-field :value="label('genders', form.gender)" label="性别" readonly clickable right-icon="arrow" @click="openPicker('gender', 'genders')" />
        <div class="native-field"><label for="lead-birthday">生日</label><input id="lead-birthday" v-model="form.birthday" type="date" /></div>
        <van-field v-model.trim="form.region" label="所在地区" maxlength="100" placeholder="省、市、区或详细地区" />
      </section>

      <section class="form-section">
        <h2>学习需求</h2>
        <van-field v-model.trim="form.studentName" label="学员姓名" maxlength="50" placeholder="与联系人相同时可不填" />
        <van-field :value="selectedLabel('relationships', form.relationship)" label="与学员关系" readonly clickable right-icon="arrow" placeholder="请选择" @click="openPicker('relationship', 'relationships')" />
        <van-field :value="selectedLabel('grades', form.grade)" label="年级" readonly clickable right-icon="arrow" placeholder="请选择" @click="openPicker('grade', 'grades')" />
        <div class="flat-choice-field">
          <div class="flat-choice-heading"><span>意向课程</span><span>已选 {{ form.courseCodes.length }} 项</span></div>
          <div v-if="courseChoices.length" class="flat-choice-list">
            <button
              v-for="item in courseChoices"
              :key="item.value"
              type="button"
              :class="['flat-choice', { selected: selectionIncludes(form.courseCodes, item.value), historical: item.historical }]"
              :disabled="isChoiceDisabled(item)"
              @click="toggleSelection('courseCodes', item.value, item)"
            ><van-icon v-if="selectionIncludes(form.courseCodes, item.value)" name="success" /><span>{{ item.label }}</span></button>
          </div>
          <div v-else class="choice-empty">暂无可选课程</div>
        </div>
        <div class="flat-choice-field">
          <div class="flat-choice-heading"><span>意向门店</span><span>已选 {{ form.campusCodes.length }} 项</span></div>
          <div v-if="campusChoices.length" class="flat-choice-list">
            <button
              v-for="item in campusChoices"
              :key="item.value"
              type="button"
              :class="['flat-choice', { selected: selectionIncludes(form.campusCodes, item.value), historical: item.historical }]"
              :disabled="isChoiceDisabled(item)"
              @click="toggleSelection('campusCodes', item.value, item)"
            ><van-icon v-if="selectionIncludes(form.campusCodes, item.value)" name="success" /><span>{{ item.label }}</span></button>
          </div>
          <div v-else class="choice-empty">暂无可选门店</div>
        </div>
      </section>

      <section class="form-section">
        <h2>业务信息</h2>
        <div class="flat-choice-field">
          <div class="flat-choice-heading"><span>企业客户标签</span><span>已选 {{ form.wecomTagIds.length }} 项</span></div>
          <div v-if="wecomTagChoiceGroups.length" class="flat-choice-groups">
            <div v-for="group in wecomTagChoiceGroups" :key="group.id" class="flat-choice-group">
              <div class="choice-group-title">{{ group.name }}</div>
              <div class="flat-choice-list">
                <button
                  v-for="item in group.items"
                  :key="item.value"
                  type="button"
                  :class="['flat-choice', { selected: selectionIncludes(form.wecomTagIds, item.value), historical: item.historical }]"
                  :disabled="isChoiceDisabled(item)"
                  @click="toggleSelection('wecomTagIds', item.value, item)"
                ><van-icon v-if="selectionIncludes(form.wecomTagIds, item.value)" name="success" /><span>{{ item.label }}</span></button>
              </div>
            </div>
          </div>
          <div v-else class="choice-empty">暂无可用企业客户标签</div>
        </div>
        <div class="tag-help">已失效标签可移除，保存时不会再次提交。</div>
        <div v-if="editing" class="owner">负责人 <span>{{ original.ownerEmployeeName || '未分配' }}</span></div>
        <div class="native-field"><label for="lead-next-follow">下次跟进</label><input id="lead-next-follow" v-model="form.nextFollowAt" type="datetime-local" /></div>
        <van-field v-model.trim="form.remark" label="备注" type="textarea" rows="3" maxlength="500" show-word-limit placeholder="记录客资背景、需求或沟通注意事项" />
      </section>
      <div v-if="submitError" class="save-error" role="alert">{{ submitError }}</div>
      <div class="submit-bar"><van-button block type="info" native-type="submit" :loading="submitting" :disabled="loading || loadError">保存客资</van-button></div>
    </van-form>

    <van-popup v-model="pickerVisible" position="bottom">
      <van-picker show-toolbar :columns="pickerChoices" @cancel="pickerVisible = false" @confirm="chooseOption" />
    </van-popup>
  </div>
</template>

<script>
import { createLead, getLeadDetail, getLeadOptions, revealLeadIdentity, updateLead } from '../../api/crmLead'
import { choicesWithHistoricalValue, choicesWithHistoricalValues, currentWecomTagIds, editableIdentity, normalizeSelection, normalizeWecomTagGroups, optionLabel, wecomTagGroupsWithHistoricalValues } from './leadView'
import { clearLeadListState } from './listState'

const OPTION_FIELDS = {
  gender: 'genders', relationship: 'relationships', grade: 'grades'
}

function routeLeadId (route) {
  const query = route.query || {}
  const hasLeadId = Object.prototype.hasOwnProperty.call(query, 'leadId')
  const hasId = Object.prototype.hasOwnProperty.call(query, 'id')
  if (!hasLeadId && !hasId) return query.mode === 'edit' ? NaN : 0
  const value = hasLeadId ? query.leadId : query.id
  const leadId = Number(value)
  if (Array.isArray(value) || !/^\d+$/.test(String(value)) || !Number.isSafeInteger(leadId) || leadId < 1) return NaN
  if (hasLeadId && hasId && String(query.id) !== String(value)) return NaN
  return leadId
}

function formFromLead (lead) {
  return {
    name: lead.name || '', mobile: editableIdentity(lead.mobile), wechatId: editableIdentity(lead.wechatId), email: editableIdentity(lead.email),
    telephone: editableIdentity(lead.telephone), gender: lead.gender === undefined ? 0 : Number(lead.gender),
    birthday: lead.birthday ? String(lead.birthday).slice(0, 10) : '', region: lead.region || '', studentName: lead.studentName || '',
    relationship: lead.relationship || '', grade: lead.grade || '',
    courseCodes: normalizeSelection(lead.courseCodes, lead.course), campusCodes: normalizeSelection(lead.campusCodes, lead.campus),
    wecomTagIds: normalizeSelection(lead.wecomTagIds || lead.tagIds),
    nextFollowAt: lead.nextFollowAt ? String(lead.nextFollowAt).replace(' ', 'T').slice(0, 16) : '', remark: lead.remark || ''
  }
}

export default {
  name: 'LeadForm',
  data () {
    return {
      leadId: routeLeadId(this.$route), loadedLeadId: null, loadVersion: 0, original: {}, options: {}, form: formFromLead({}),
      loading: true, loadError: false, loadErrorMessage: '', submitting: false, submitError: '', clearWechatId: false,
      mobileRevealValue: '', mobileRevealError: '',
      pickerVisible: false, pickerField: '', pickerKey: '', pickerChoices: []
    }
  },
  computed: {
    editing () { return this.leadId !== 0 },
    hasStoredWechatId () {
      const value = String(this.original.wechatId || '')
      return Boolean(this.editing && value && value !== '-')
    },
    wechatIdPlaceholder () {
      if (this.hasStoredWechatId && !this.form.wechatId) return `当前 ${this.original.wechatId}，输入新微信号可替换`
      return '选填，用于记录客户微信号'
    },
    normalizedWecomTagGroups () {
      return normalizeWecomTagGroups(this.options.wecomTagGroups)
    },
    courseChoices () { return choicesWithHistoricalValues(this.options, 'courses', this.form.courseCodes) },
    campusChoices () { return choicesWithHistoricalValues(this.options, 'campuses', this.form.campusCodes) },
    wecomTagChoiceGroups () { return wecomTagGroupsWithHistoricalValues(this.normalizedWecomTagGroups, this.form.wecomTagIds) }
  },
  watch: {
    '$route.query' () {
      if (this.$route.path === '/crmLead/form') this.loadPage()
    }
  },
  created () { this.loadPage() },
  beforeRouteLeave (to, from, next) {
    if (!['/customer', '/crmLead/detail'].includes(to.path)) clearLeadListState()
    next()
  },
  methods: {
    label (key, value) { return optionLabel(this.options, key, value) },
    selectedLabel (key, value) { return value === '' || value === null || value === undefined ? '' : this.label(key, value) },
    goBack () {
      this.$router.push(this.leadId > 0 ? { path: '/crmLead/detail', query: { leadId: this.leadId } } : { path: '/customer', query: { tab: 'lead' } })
    },
    validMobile (value) {
      if (value) return /^\+?\d{6,20}$/.test(String(value).replace(/[\s-]/g, ''))
      return Boolean(this.editing && String(this.original.mobile || '').includes('*'))
    },
    validEmail (value) { return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) },
    async loadPage () {
      const loadVersion = ++this.loadVersion
      const leadId = routeLeadId(this.$route)
      this.leadId = leadId
      this.loadedLeadId = null
      this.loading = true
      this.loadError = false
      this.loadErrorMessage = ''
      this.submitError = ''
      this.pickerVisible = false
      try {
        if (!Number.isSafeInteger(leadId)) throw new Error('客资编号无效，请返回列表重新打开编辑页')
        const choicesRequest = getLeadOptions()
        const detailRequest = leadId > 0 ? getLeadDetail(leadId) : Promise.resolve({ data: {} })
        const [choices, detail] = await Promise.all([choicesRequest, detailRequest])
        if (loadVersion !== this.loadVersion) return
        const original = detail.data || {}
        if (leadId > 0 && (Number(original.leadId || original.id) !== leadId || !Number.isSafeInteger(Number(original.version)) || Number(original.version) < 1)) {
          throw new Error('客资编辑资料不完整，请重新加载后保存')
        }
        this.options = choices.data || {}
        this.original = original
        this.form = formFromLead(this.original)
        this.loadedLeadId = leadId
        this.clearWechatId = false
        this.mobileRevealValue = ''
        this.mobileRevealError = ''
        await this.revealMobileForEdit()
      } catch (error) {
        if (loadVersion !== this.loadVersion) return
        this.loadError = true
        this.loadErrorMessage = error.message || '客资资料加载失败，请重新加载'
      } finally {
        if (loadVersion === this.loadVersion) this.loading = false
      }
    },
    async revealMobileForEdit () {
      if (!this.editing || !String(this.original.mobile || '').includes('*')) return
      const leadId = this.leadId
      const original = this.original
      try {
        const response = await revealLeadIdentity({ leadId, type: 'mobile' })
        if (this.leadId !== leadId || this.original !== original) return
        const value = String((response.data || {}).value || '').trim()
        if (!value || value.includes('*')) throw new Error('手机号揭示结果无效')
        this.mobileRevealValue = value
        this.form.mobile = value
      } catch (error) {
        if (this.leadId !== leadId || this.original !== original) return
        this.mobileRevealError = '完整手机号加载失败，仍可编辑其他资料；手机号保持不变且不会提交脱敏值。'
        this.$toast(this.mobileRevealError)
      }
    },
    openPicker (field, key) {
      if (OPTION_FIELDS[field] !== key) return
      const originalValue = this.editing ? this.original[field] : ''
      const choices = choicesWithHistoricalValue(this.options, key, originalValue)
      if (!choices.length) {
        this.$toast('暂无可选项，请先在管理后台配置字典')
        return
      }
      this.pickerField = field
      this.pickerKey = key
      const canClear = ['relationship', 'grade'].includes(field)
      this.pickerChoices = [...(canClear ? [{ text: '不选择', value: '' }] : []), ...choices.map(item => ({ text: item.label, value: item.value }))]
      this.pickerVisible = true
    },
    chooseOption (choice) {
      this.form[this.pickerField] = choice.value
      this.pickerVisible = false
    },
    onWechatIdInput (value) {
      if (value) this.clearWechatId = false
    },
    onClearWechatIdChange (checked) {
      if (checked) this.form.wechatId = ''
    },
    selectionIncludes (values, value) {
      return Array.isArray(values) && values.some(item => String(item) === String(value))
    },
    isChoiceDisabled (item) {
      return Boolean(item && (item.disabled === true || item.enabled === false))
    },
    toggleSelection (field, value, item) {
      if (this.isChoiceDisabled(item)) return
      const values = Array.isArray(this.form[field]) ? this.form[field].slice() : []
      const index = values.findIndex(existing => String(existing) === String(value))
      if (index >= 0) {
        values.splice(index, 1)
      } else values.push(value)
      this.form[field] = values
    },
    buildPayload () {
      const originalNextFollow = String(this.original.nextFollowAt || '')
      const nextFollowAt = this.form.nextFollowAt
        ? (this.editing && this.form.nextFollowAt === originalNextFollow.replace(' ', 'T').slice(0, 16)
            ? originalNextFollow : this.form.nextFollowAt.replace('T', ' ') + ':00')
        : ''
      const payload = { ...this.form, nextFollowAt }
      payload.wecomTagIds = currentWecomTagIds(this.normalizedWecomTagGroups, this.form.wecomTagIds)
      for (const field of ['mobile', 'wechatId', 'email', 'telephone']) {
        if (field === 'wechatId' && this.clearWechatId) {
          payload.wechatId = ''
          continue
        }
        const originalValue = String(this.original[field] || '')
        if (String(payload[field] || '').includes('*')) {
          delete payload[field]
          continue
        }
        if (field === 'mobile' && this.mobileRevealValue && String(payload.mobile || '') === this.mobileRevealValue) {
          delete payload.mobile
          continue
        }
        const shouldPreserveProtectedValue = this.editing && originalValue.includes('*')
        if (!payload[field] && (!this.editing || !originalValue || shouldPreserveProtectedValue)) delete payload[field]
      }
      if (this.editing) {
        payload.leadId = this.leadId
        payload.version = this.original.version
      }
      return payload
    },
    async submit () {
      if (this.submitting || this.loading) return
      const leadId = routeLeadId(this.$route)
      const editing = leadId > 0
      if (!Number.isSafeInteger(leadId) || this.loadError || this.leadId !== leadId || this.loadedLeadId !== leadId ||
          (editing && (Number(this.original.leadId || this.original.id) !== leadId || !Number.isSafeInteger(Number(this.original.version)) || Number(this.original.version) < 1))) {
        this.submitError = '客资资料尚未正确加载，请重新加载后保存'
        this.$toast.fail(this.submitError)
        return
      }
      const payload = this.buildPayload()
      const loadVersion = this.loadVersion
      this.submitError = ''
      this.submitting = true
      try {
        const response = editing ? await updateLead(payload) : await createLead(payload)
        if (loadVersion !== this.loadVersion) return
        const responseData = response.data || {}
        const savedLeadId = Number(responseData.leadId || responseData.id || leadId)
        if (!Number.isSafeInteger(savedLeadId) || savedLeadId < 1) throw new Error('保存结果缺少客资编号，请刷新列表确认')
        const customerMatchMessage = responseData.customerMatch && responseData.customerMatch.message
        const saveMessage = responseData.reusedExisting
          ? (responseData.message || '已关联现有客资并加载最新资料')
          : '客资已保存'
        if (customerMatchMessage) this.$toast(`${saveMessage}；${customerMatchMessage}`)
        else this.$toast.success(saveMessage)
        this.$router.replace({ path: '/crmLead/detail', query: { leadId: savedLeadId } })
      } catch (error) {
        if (loadVersion !== this.loadVersion) return
        const responseData = error && error.response && error.response.data
        this.submitError = (responseData && (responseData.msg || responseData.message)) || error.message || '保存客资失败，请稍后重试'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.lead-form-page { min-height: 100vh; padding-bottom: calc(92px + env(safe-area-inset-bottom)); background: #f2f4f6; }
.lead-form-page /deep/ .van-nav-bar { position: sticky; top: 0; z-index: 2; }
.sync-notice { display: flex; align-items: flex-start; gap: 6px; padding: 10px 16px; background: #edf6ff; color: #3d6685; font-size: 12px; line-height: 18px; }
.form-state { padding: 60px 16px; text-align: center; }
.form-section { margin-top: 10px; background: #fff; }
.form-section h2 { margin: 0; padding: 14px 16px 8px; color: #25313a; font-size: 15px; font-weight: 600; }
.form-section /deep/ .van-field__label { width: 90px; color: #55636d; }
.form-section /deep/ .van-field__control { text-align: right; }
.form-section /deep/ .van-field__body { min-width: 0; }
.form-section /deep/ .van-field__control[readonly] { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tag-help { padding: 0 16px 10px 106px; background: #fff; color: #969799; font-size: 12px; line-height: 18px; }
.wechat-clear-row /deep/ .van-cell__title { color: #55636d; }
.identity-clear-help { padding: 0 16px 10px 106px; background: #fff; color: #ee6a3a; font-size: 12px; line-height: 18px; }
.identity-warning { padding: 0 16px 10px 106px; background: #fff; color: #b54b36; font-size: 12px; line-height: 18px; overflow-wrap: anywhere; }
.save-error { padding: 12px 16px; color: #b54b36; font-size: 13px; line-height: 20px; overflow-wrap: anywhere; }
.native-field, .owner { display: flex; align-items: center; justify-content: space-between; min-height: 44px; gap: 10px; padding: 8px 16px; border-bottom: 1px solid #f0f1f2; background: #fff; color: #55636d; font-size: 14px; }
.native-field label { flex: 0 0 90px; }
.native-field input { flex: 1; min-width: 0; padding: 7px; border: 1px solid #e6e9eb; background: #fff; color: #323b42; font: inherit; }
.owner span { color: #323b42; }
.submit-bar { position: fixed; right: 0; bottom: 0; left: 0; z-index: 2; max-width: 720px; margin: auto; padding: 9px 16px calc(9px + env(safe-area-inset-bottom)); border-top: 1px solid #e8ebed; background: #fff; }
.flat-choice-field { padding: 12px 16px 13px; border-bottom: 1px solid #f0f1f2; background: #fff; }
.flat-choice-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 9px; color: #55636d; font-size: 14px; }
.flat-choice-heading span:last-child { flex: none; color: #969799; font-size: 12px; }
.flat-choice-list { display: flex; flex-wrap: wrap; gap: 8px; min-height: 32px; }
.flat-choice { display: inline-flex; align-items: center; gap: 4px; max-width: 100%; min-height: 32px; padding: 5px 10px; border: 1px solid #d7dde1; border-radius: 3px; background: #fff; color: #4f5d66; font: inherit; font-size: 13px; line-height: 20px; text-align: left; white-space: normal; overflow-wrap: anywhere; }
.flat-choice span { min-width: 0; overflow-wrap: anywhere; }
.flat-choice.selected { border-color: #1989fa; background: #edf6ff; color: #1269a7; }
.flat-choice.historical { border-style: dashed; color: #7a858c; }
.flat-choice:disabled { border-color: #e8ebed; background: #f5f6f7; color: #a3aaaf; }
.flat-choice-group + .flat-choice-group { margin-top: 12px; }
.choice-group-title { margin-bottom: 7px; color: #7a858c; font-size: 12px; line-height: 18px; }
.choice-empty { min-height: 32px; color: #969799; font-size: 13px; line-height: 32px; }
</style>
